import React, { useState } from "react";
import "@/App.css";
import {
  List,
  X,
  MagnifyingGlass,
  Phone,
  EnvelopeSimple,
  MapPin,
  Clock,
  CaretRight,
  Funnel,
  Disc,
  Gauge,
  Engine,
  Wrench,
  Drop,
  Lightning,
  Snowflake,
  Gear,
  ArrowRight,
  CheckCircle,
  ShieldCheck,
  Package,
  Headset,
  WhatsappLogo,
  ArrowSquareOut,
  EnvelopeOpen,
} from "@phosphor-icons/react";

const WEBKATALOG_URL = "https://mikramatik.bestparts.cloud/";
const PHONE_MOBILE = "091/212-4188";
const PHONE_MOBILE_TEL = "+385912124188";
const PHONE_LAND = "01/2338-550";
const PHONE_LAND_TEL = "+38512338550";
const WHATSAPP_URL = "https://wa.me/385912124188";
const CONTACT_EMAIL = "info@mikramatik.hr";

// ----------------- Data -----------------
const NAV_LINKS = [
  { id: "home", label: "Početna" },
  { id: "webkatalog", label: "Webkatalog" },
  { id: "about", label: "O nama" },
  { id: "catalog", label: "Asortiman" },
  { id: "inquiry", label: "VIN Upit" },
  { id: "contact", label: "Kontakt" },
];

const PARTS_CATALOG = [
  { name: "Filteri", icon: Funnel, slug: "filteri" },
  { name: "Disk pločice i pakne", icon: Disc, slug: "disk-plocice" },
  { name: "Amortizeri", icon: Gauge, slug: "amortizeri" },
  { name: "Kvačila i zamašnjaci", icon: Gear, slug: "kvacila" },
  { name: "Homokinetički zglobovi", icon: Wrench, slug: "homokineticki-zglobovi" },
  { name: "Dijelovi ovjesa", icon: Wrench, slug: "dijelovi-ovjesa" },
  { name: "Ležajevi", icon: Gear, slug: "lezajevi" },
  { name: "Remenje", icon: Gear, slug: "remenje" },
  { name: "Senzori", icon: Lightning, slug: "senzori" },
  { name: "Podizači stakala", icon: Wrench, slug: "podizaci-stakala" },
  { name: "Webasto", icon: Snowflake, slug: "webasto" },
  { name: "Dijelovi limarije", icon: Wrench, slug: "dijelovi-limarije" },
  { name: "Motorna ulja", icon: Drop, slug: "motorna-ulja" },
  { name: "Svjećice i grijači", icon: Lightning, slug: "svjecice" },
  { name: "Pumpe vode", icon: Drop, slug: "pumpe-vode" },
  { name: "Poluosovine", icon: Wrench, slug: "poluosovine" },
  { name: "Hladnjaci", icon: Snowflake, slug: "hladnjaci" },
  { name: "Termostati", icon: Gauge, slug: "termostati" },
  { name: "AGR ventili", icon: Engine, slug: "agr-ventili" },
  { name: "Mjerači protoka zraka", icon: Gauge, slug: "mjeraci-protoka" },
  { name: "Brtve", icon: Wrench, slug: "brtve" },
  { name: "Pneumatika", icon: Disc, slug: "pneumatika" },
  { name: "Komponente motora", icon: Engine, slug: "komponente-motora" },
  { name: "Akumulatori", icon: Lightning, slug: "akumulatori" },
];

const TRUST_BRANDS = [
  "BOSCH", "MANN-FILTER", "SACHS", "FEBI", "TRW", "VALEO", "MAHLE",
  "HELLA", "NGK", "CASTROL", "MOBIL", "ZF", "LUK", "CONTINENTAL",
];

// ----------------- Components -----------------
const Header = ({ active }) => {
  const [open, setOpen] = useState(false);

  const handleNav = (id) => {
    setOpen(false);
    if (id === "webkatalog") {
      window.open(WEBKATALOG_URL, "_blank", "noopener,noreferrer");
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-black border-b"
      style={{ borderColor: "var(--mm-border)" }}
      data-testid="site-header"
    >
      {/* Top row: contact actions + centered logo */}
      <div className="container-pad relative flex items-center justify-between h-28 md:h-36 lg:h-40">
        {/* Left: contact actions */}
        <div className="flex items-center gap-3 z-10">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-3 px-6 py-4 border-2 border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366] hover:text-black transition-colors font-display uppercase text-lg font-bold tracking-wider"
            data-testid="header-whatsapp-btn"
          >
            <WhatsappLogo size={26} weight="fill" />
            WhatsApp
          </a>
        </div>

        {/* Center: Logo (absolute centered) */}
        <button
          onClick={() => handleNav("home")}
          className="absolute left-1/2 -translate-x-1/2 flex items-center group"
          data-testid="logo-button"
          aria-label="Mikra Matik — Početna"
        >
          <img
            src="/assets/logo.png?v=5"
            alt="Mikra Matik Autodijelovi d.o.o."
            className="h-20 md:h-28 lg:h-32 w-auto object-contain"
          />
        </button>

        {/* Right: phone + mobile menu toggle */}
        <div className="flex items-center gap-3 z-10">
          <a
            href={`tel:${PHONE_MOBILE_TEL}`}
            className="hidden md:inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#D63026] transition-colors text-white px-6 py-4 font-display uppercase text-lg font-bold tracking-wider"
            data-testid="header-call-btn"
          >
            <Phone size={24} weight="bold" /> {PHONE_MOBILE}
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>

      {/* Bottom row: nav (desktop only) */}
      <nav
        className="hidden lg:flex items-center justify-center gap-10 border-t py-4"
        style={{ borderColor: "var(--mm-border)" }}
        data-testid="primary-nav"
      >
        {NAV_LINKS.map((l) => (
          <button
            key={l.id}
            onClick={() => handleNav(l.id)}
            className="font-display uppercase text-base xl:text-lg tracking-wider text-zinc-200 hover:text-[#FF3B30] transition-colors font-bold"
            data-testid={`nav-${l.id}-link`}
          >
            {l.label}
          </button>
        ))}
      </nav>

      {/* Mobile nav */}
      {open && (
        <div className="lg:hidden border-t" style={{ borderColor: "var(--mm-border)" }}>
          <div className="container-pad py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => handleNav(l.id)}
                className="text-left font-display uppercase text-lg tracking-wider text-zinc-200 hover:text-[#FF3B30] py-2 font-bold"
                data-testid={`mobile-nav-${l.id}-link`}
              >
                {l.label}
              </button>
            ))}
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#25D366]/40 text-[#25D366] font-display uppercase text-base font-bold tracking-wider"
            >
              <WhatsappLogo size={20} weight="fill" /> WhatsApp
            </a>
            <a
              href={`tel:${PHONE_MOBILE_TEL}`}
              className="mm-btn-primary"
            >
              <Phone size={18} weight="bold" /> {PHONE_MOBILE}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => {
  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-[92vh] flex items-end pt-36 lg:pt-56 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/11455717/pexels-photo-11455717.jpeg"
          alt="Engine"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/55" />
        <div className="absolute inset-0 stripe-pattern opacity-40" />
      </div>

      <div className="container-pad relative z-10 w-full pb-16 md:pb-24">
        <div className="max-w-5xl">
          <div className="reveal flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#FF3B30]" />
            <span className="font-display uppercase tracking-[0.3em] text-xs text-[#FF3B30] font-bold">
              Sesvete · Zagreb · Hrvatska
            </span>
          </div>

          <h1
            className="reveal reveal-delay-1 sr-only"
            data-testid="hero-title"
          >
            Mikra Matik Autodijelovi — Sesvete, Zagreb
          </h1>

          <p
            className="reveal reveal-delay-2 mt-8 text-zinc-300 max-w-2xl text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            Već desetljećima isporučujemo originalne i provjerene aftermarket
            dijelove za osobna i gospodarska vozila — od Sesveta do cijele
            Hrvatske. Brzo, pouzdano, profesionalno.
          </p>

          <div className="reveal reveal-delay-3 mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => handleNav("inquiry")}
              className="mm-btn-primary"
              data-testid="hero-inquiry-btn"
            >
              <MagnifyingGlass size={18} weight="bold" />
              Pošalji upit za dio
            </button>
            <a
              href={WEBKATALOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mm-btn-outline"
              data-testid="hero-webkatalog-btn"
            >
              Otvori Webkatalog
              <ArrowSquareOut size={18} weight="bold" />
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 max-w-3xl">
            {[
              { v: "10.000+", k: "Artikala na zalihi" },
              { v: "30+", k: "Godina iskustva" },
              { v: "500+", k: "Aktivnih klijenata" },
              { v: "24h", k: "Brza isporuka" },
            ].map((s, i) => (
              <div key={i} className="bg-[#0A0A0A] p-5">
                <div className="font-display font-black text-3xl text-white">
                  {s.v}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  {s.k}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustStrip = () => (
  <div
    className="border-y overflow-hidden bg-[#0A0A0A] py-6"
    style={{ borderColor: "var(--mm-border)" }}
    data-testid="trust-strip"
  >
    <div className="flex marquee-track w-max gap-16 px-6">
      {[...TRUST_BRANDS, ...TRUST_BRANDS].map((b, i) => (
        <span
          key={i}
          className="font-display font-bold uppercase tracking-widest text-zinc-500 text-xl whitespace-nowrap"
        >
          {b}
        </span>
      ))}
    </div>
  </div>
);

const About = () => (
  <section id="about" className="section-spacing relative" data-testid="about-section">
    <div className="container-pad grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-7">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-[#FF3B30]" />
          <span className="font-display uppercase tracking-[0.3em] text-xs text-[#FF3B30] font-bold">
            O nama
          </span>
        </div>
        <h2 className="font-display font-black uppercase text-white text-4xl md:text-6xl leading-[0.95] mb-8">
          Pravi dio.
          <br />
          Pravo vrijeme.
          <br />
          <span className="text-zinc-500">Pravo mjesto.</span>
        </h2>
        <div className="space-y-5 text-zinc-300 text-lg leading-relaxed max-w-2xl">
          <p>
            <span className="text-white font-semibold">Mikra Matik Autodijelovi d.o.o.</span> dugogodišnji
            je dobavljač rezervnih dijelova za osobna i gospodarska vozila u Hrvatskoj.
            Sjedište nam je u Sesvetama, a poslujemo s tisućama klijenata diljem zemlje —
            od pojedinaca do velikih voznih parkova.
          </p>
          <p>
            Naša snaga je u <span className="text-white">brzini, dostupnosti i stručnom savjetovanju</span>.
            Surađujemo isključivo s renomiranim svjetskim proizvođačima dijelova kako bismo
            osigurali kvalitetu koja traje.
          </p>
        </div>
      </div>

      <div className="lg:col-span-5 lg:sticky lg:top-28 grid grid-cols-1 gap-px bg-white/10">
        {[
          { icon: Package, t: "Veliki asortiman", d: "Tisuće artikala dostupno odmah s lagera." },
          { icon: ShieldCheck, t: "Provjereni dobavljači", d: "Bosch, Mann, Sachs, Febi, TRW i drugi." },
          { icon: Headset, t: "Stručni savjet", d: "Naš tim pomaže pronaći točan dio za vaše vozilo." },
          { icon: Clock, t: "Brza isporuka", d: "Dostava u Zagreb i okolicu istog ili idućeg dana." },
        ].map((f, i) => {
          const Ic = f.icon;
          return (
            <div
              key={i}
              className="bg-[#161618] p-7 flex items-start gap-4 hover:bg-[#1c1c1f] transition-colors"
              data-testid={`feature-${i}`}
            >
              <div className="shrink-0 w-12 h-12 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center">
                <Ic size={22} weight="bold" className="text-[#FF3B30]" />
              </div>
              <div>
                <div className="font-display uppercase font-bold text-white tracking-wide">
                  {f.t}
                </div>
                <div className="text-zinc-400 text-sm mt-1.5">{f.d}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Catalog = () => (
  <section id="catalog" className="section-spacing" data-testid="catalog-section">
    <div className="container-pad">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#FF3B30]" />
            <span className="font-display uppercase tracking-[0.3em] text-xs text-[#FF3B30] font-bold">
              Asortiman
            </span>
          </div>
          <h2 className="font-display font-black uppercase text-white text-4xl md:text-5xl leading-[0.95]">
            Sve grupe
            <br />
            <span className="text-[#FF3B30]">rezervnih dijelova.</span>
          </h2>
        </div>
        <p className="text-zinc-400 max-w-md">
          Pretražite po kategoriji ili nas kontaktirajte za specifičan dio — radimo i
          po VIN broju vozila.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-px bg-white/10">
        {PARTS_CATALOG.map((p, i) => {
          const Ic = p.icon;
          const imgSrc = `/parts/${p.slug}.png`;
          return (
            <button
              key={i}
              className="bg-[#0A0A0A] hover:bg-[#161618] flex flex-col items-stretch group transition-colors text-left overflow-hidden"
              data-testid={`part-category-${i}`}
              onClick={() => {
                const el = document.getElementById("inquiry");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              <div className="relative aspect-square bg-[#0A0A0A] overflow-hidden border-b border-white/5">
                <img
                  src={imgSrc}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
                <div className="absolute top-3 left-3 w-9 h-9 bg-black/70 backdrop-blur border border-white/10 flex items-center justify-center pointer-events-none">
                  <Ic
                    size={18}
                    weight="duotone"
                    className="text-[#FF3B30]"
                  />
                </div>
              </div>
              <div className="p-4 flex items-center justify-between gap-2">
                <span className="font-display uppercase text-white text-sm font-bold tracking-tight leading-tight">
                  {p.name}
                </span>
                <CaretRight
                  size={14}
                  className="shrink-0 text-zinc-700 group-hover:text-[#FF3B30] transition-colors"
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  </section>
);

const Webkatalog = () => (
  <section
    id="webkatalog"
    className="section-spacing bg-[#0c0c0e] relative overflow-hidden"
    data-testid="webkatalog-section"
  >
    <div className="absolute inset-0 stripe-pattern opacity-30 pointer-events-none" />
    <div className="container-pad relative">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#FF3B30]" />
            <span className="font-display uppercase tracking-[0.3em] text-xs text-[#FF3B30] font-bold">
              Online katalog
            </span>
          </div>
          <h2 className="font-display font-black uppercase text-white text-4xl md:text-6xl leading-[0.95] mb-6">
            Webkatalog
            <br />
            <span className="text-[#FF3B30]">10.000+ artikala</span>
            <br />
            online.
          </h2>
          <p className="text-zinc-300 text-lg leading-relaxed max-w-2xl mb-8">
            Pretražujte naš kompletan katalog dijelova online — po marki vozila,
            kategoriji ili kataloškom broju. Provjerite dostupnost i cijene 24/7.
          </p>

          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-6 mb-8 max-w-2xl">
            <div className="flex items-start gap-4">
              <EnvelopeOpen size={28} weight="duotone" className="text-[#FF3B30] shrink-0 mt-1" />
              <div>
                <div className="font-display uppercase font-bold text-white text-lg tracking-tight mb-2">
                  Registracija za pristup
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  Za pristup Webkatalogu i pregled cijena potrebna je registracija.
                  Pošaljite zahtjev na{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}?subject=Zahtjev%20za%20registraciju%20-%20Webkatalog`}
                    className="text-[#FF3B30] font-semibold hover:underline"
                    data-testid="webkatalog-email-link"
                  >
                    {CONTACT_EMAIL}
                  </a>{" "}
                  i u kratkom roku ćemo vam dostaviti pristupne podatke.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href={WEBKATALOG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mm-btn-primary"
              data-testid="webkatalog-open-btn"
            >
              Otvori Webkatalog
              <ArrowSquareOut size={18} weight="bold" />
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Zahtjev%20za%20registraciju%20-%20Webkatalog`}
              className="mm-btn-outline"
              data-testid="webkatalog-register-btn"
            >
              <EnvelopeSimple size={18} weight="bold" />
              Zatraži registraciju
            </a>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-[#161618] border border-white/10 p-1">
            <div className="bg-[#0A0A0A] p-8">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                  <div className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                  <div className="w-3 h-3 rounded-full bg-[#28C840]" />
                </div>
                <div className="text-zinc-500 text-xs font-mono">mikramatik.bestparts.cloud</div>
              </div>
              <div className="space-y-3">
                {[
                  { k: "Pretraga po marki", v: "Sve marke" },
                  { k: "Cross-reference", v: "Uključeno" },
                  { k: "Realtime stanje", v: "24/7" },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2 border-b border-white/5"
                  >
                    <span className="text-zinc-400 text-sm">{row.k}</span>
                    <span className="text-[#FF3B30] font-display uppercase text-sm font-bold tracking-wider">
                      {row.v}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);


const InquiryForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vin: "",
    vehicle_model: "",
    part_needed: "",
    notes: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const lines = [
      `Ime i prezime: ${form.name}`,
      `Email: ${form.email}`,
      `Telefon: ${form.phone || "-"}`,
      `VIN broj šasije: ${form.vin || "-"}`,
      `Marka i model vozila: ${form.vehicle_model}`,
      `Traženi dio: ${form.part_needed}`,
      "",
      "Napomene:",
      form.notes || "-",
    ];
    const subject = `Upit za dio — ${form.vehicle_model || "vozilo"}`;
    const body = lines.join("\n");
    const url = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <section
      id="inquiry"
      className="section-spacing bg-[#0c0c0e] relative"
      data-testid="inquiry-section"
    >
      <div className="container-pad">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left side */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10 bg-[#FF3B30]" />
              <span className="font-display uppercase tracking-[0.3em] text-xs text-[#FF3B30] font-bold">
                VIN Upit
              </span>
            </div>
            <h2 className="font-display font-black uppercase text-white text-4xl md:text-5xl leading-[0.95] mb-6">
              Pošaljite upit
              <br />
              za točan dio.
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed mb-8">
              Pošaljite nam VIN broj vašeg vozila ili točan model i pronaći ćemo
              odgovarajući dio. Odgovor obično šaljemo unutar nekoliko sati radnim
              danom.
            </p>
            <ul className="space-y-3">
              {[
                "Provjera dostupnosti odmah",
                "Cijena i rok isporuke u jednom emailu",
                "Mogućnost dostave ili preuzimanja",
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle size={20} weight="fill" className="text-[#FF3B30]" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right side - form */}
          <div className="lg:col-span-7">
            <div
              className="border-l-2 border-[#FF3B30] bg-[#161618] p-8 md:p-10"
              data-testid="inquiry-form-card"
            >
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="mm-label">Ime i prezime *</label>
                  <input
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Ivan Horvat"
                    className="mm-input"
                    data-testid="inquiry-name-input"
                  />
                </div>
                <div>
                  <label className="mm-label">Email *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="ivan@primjer.hr"
                    className="mm-input"
                    data-testid="inquiry-email-input"
                  />
                </div>
                <div>
                  <label className="mm-label">Telefon</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="+385 91 234 5678"
                    className="mm-input"
                    data-testid="inquiry-phone-input"
                  />
                </div>
                <div>
                  <label className="mm-label">VIN broj šasije</label>
                  <input
                    name="vin"
                    value={form.vin}
                    onChange={handleChange}
                    placeholder="WAUZZZ8K8AA000000"
                    className="mm-input uppercase"
                    data-testid="inquiry-vin-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mm-label">Marka i model vozila *</label>
                  <input
                    name="vehicle_model"
                    required
                    value={form.vehicle_model}
                    onChange={handleChange}
                    placeholder="VW Golf 1.9 TDI 2008."
                    className="mm-input"
                    data-testid="inquiry-vehicle-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mm-label">Traženi dio *</label>
                  <input
                    name="part_needed"
                    required
                    value={form.part_needed}
                    onChange={handleChange}
                    placeholder="Disk pločice — prednje"
                    className="mm-input"
                    data-testid="inquiry-part-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mm-label">Dodatne napomene</label>
                  <textarea
                    name="notes"
                    rows="3"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Bilo kakva dodatna informacija koju možete podijeliti..."
                    className="mm-input resize-none"
                    data-testid="inquiry-notes-input"
                  />
                </div>

                <div className="md:col-span-2 flex flex-wrap items-center gap-4 pt-2">
                  <button
                    type="submit"
                    className="mm-btn-primary"
                    data-testid="inquiry-submit-btn"
                  >
                    Pošalji upit emailom
                    <ArrowRight size={18} weight="bold" />
                  </button>
                  <span className="text-zinc-500 text-sm">
                    Otvorit će se vaš email program s pre-popunjenim podacima.
                  </span>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const lines = [
      `Ime: ${form.name}`,
      `Email: ${form.email}`,
      `Telefon: ${form.phone || "-"}`,
      "",
      "Poruka:",
      form.message,
    ];
    const subject = `Upit s web stranice — ${form.name}`;
    const body = lines.join("\n");
    const url = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <section
      id="contact"
      className="section-spacing bg-[#0c0c0e]"
      data-testid="contact-section"
    >
      <div className="container-pad">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-10 bg-[#FF3B30]" />
          <span className="font-display uppercase tracking-[0.3em] text-xs text-[#FF3B30] font-bold">
            Kontakt
          </span>
        </div>
        <h2 className="font-display font-black uppercase text-white text-4xl md:text-5xl leading-[0.95] mb-12">
          Posjetite nas
          <br />
          ili pišite.
        </h2>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Info + form */}
          <div className="lg:col-span-7 space-y-8">
            <div className="grid sm:grid-cols-2 gap-px bg-white/10">
              <div className="bg-[#161618] p-6">
                <MapPin size={22} weight="duotone" className="text-[#FF3B30] mb-3" />
                <div className="mm-label !mb-1">Adresa</div>
                <div className="text-white">Savska cesta 85</div>
                <div className="text-white">10360 Sesvete</div>
              </div>
              <div className="bg-[#161618] p-6">
                <Phone size={22} weight="duotone" className="text-[#FF3B30] mb-3" />
                <div className="mm-label !mb-1">Telefon</div>
                <a
                  href={`tel:${PHONE_MOBILE_TEL}`}
                  className="block text-white hover:text-[#FF3B30] transition-colors"
                  data-testid="contact-mobile-link"
                >
                  Mob: {PHONE_MOBILE}
                </a>
                <a
                  href={`tel:${PHONE_LAND_TEL}`}
                  className="block text-white hover:text-[#FF3B30] transition-colors text-sm mt-1"
                  data-testid="contact-landline-link"
                >
                  Tel: {PHONE_LAND}
                </a>
              </div>
              <div className="bg-[#161618] p-6">
                <EnvelopeSimple size={22} weight="duotone" className="text-[#FF3B30] mb-3" />
                <div className="mm-label !mb-1">Email</div>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="text-white hover:text-[#FF3B30] transition-colors"
                  data-testid="contact-email-link"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="bg-[#161618] p-6">
                <Clock size={22} weight="duotone" className="text-[#FF3B30] mb-3" />
                <div className="mm-label !mb-1">Radno vrijeme</div>
                <div className="text-white text-sm">Pon–Pet · 09:00 – 17:00</div>
                <div className="text-white text-sm">Subota · 09:00 – 13:00</div>
                <div className="text-zinc-500 text-xs mt-1">Nedjelja zatvoreno</div>
              </div>
              <div className="bg-[#161618] p-6 sm:col-span-2 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#25D366]/10 border border-[#25D366]/40 flex items-center justify-center">
                    <WhatsappLogo size={24} weight="fill" className="text-[#25D366]" />
                  </div>
                  <div>
                    <div className="mm-label !mb-1">WhatsApp</div>
                    <div className="text-white">Brži kontakt — pošaljite poruku</div>
                  </div>
                </div>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 bg-[#25D366] text-black hover:bg-[#1ebe5b] transition-colors font-display uppercase text-sm font-bold tracking-wider"
                  data-testid="contact-whatsapp-btn"
                >
                  <WhatsappLogo size={18} weight="fill" />
                  Otvori WhatsApp
                </a>
              </div>
            </div>

            {/* Contact form */}
            <form
              onSubmit={handleSubmit}
              className="bg-[#161618] p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
              data-testid="contact-form"
            >
              <div>
                <label className="mm-label">Ime *</label>
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  className="mm-input"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="mm-label">Email *</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="mm-input"
                  data-testid="contact-email-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mm-label">Telefon</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="mm-input"
                  data-testid="contact-phone-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="mm-label">Poruka *</label>
                <textarea
                  name="message"
                  required
                  rows="4"
                  value={form.message}
                  onChange={handleChange}
                  className="mm-input resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              <div className="md:col-span-2 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  className="mm-btn-primary"
                  data-testid="contact-submit-btn"
                >
                  Pošalji emailom
                  <ArrowRight size={18} weight="bold" />
                </button>
                <span className="text-zinc-500 text-sm">
                  Otvorit će se vaš email program.
                </span>
              </div>
            </form>
          </div>

          {/* Map */}
          <div className="lg:col-span-5">
            <div
              className="border h-full min-h-[480px] relative overflow-hidden"
              style={{ borderColor: "var(--mm-border)" }}
              data-testid="map-container"
            >
              <iframe
                title="Mikra Matik lokacija"
                src="https://www.google.com/maps?q=Savska+cesta+85,+10360+Sesvete,+Hrvatska&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "480px", filter: "grayscale(0.6) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute top-4 left-4 bg-[#0A0A0A] border border-[#FF3B30] px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">
                  Lokacija
                </div>
                <div className="text-white font-display uppercase font-bold text-lg leading-tight">
                  Savska 85, Sesvete
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer
    className="border-t bg-[#0A0A0A]"
    style={{ borderColor: "var(--mm-border)" }}
    data-testid="site-footer"
  >
    <div className="container-pad py-12 grid md:grid-cols-3 gap-8">
      <div>
        <img
          src="/assets/logo.png?v=5"
          alt="Mikra Matik"
          className="h-20 w-auto object-contain mb-4"
        />
        <p className="text-zinc-400 text-base max-w-xs leading-relaxed">
          Autodijelovi za osobna i gospodarska vozila. Sesvete · Hrvatska.
        </p>
      </div>
      <div>
        <div className="mm-label mb-4">Brzi linkovi</div>
        <ul className="space-y-2">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => {
                  if (l.id === "webkatalog") {
                    window.open(WEBKATALOG_URL, "_blank", "noopener,noreferrer");
                    return;
                  }
                  const el = document.getElementById(l.id);
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="text-zinc-400 hover:text-[#FF3B30] text-sm transition-colors"
                data-testid={`footer-${l.id}-link`}
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="mm-label mb-4">Kontakt</div>
        <div className="text-zinc-400 text-sm space-y-2">
          <div>Savska cesta 85, 10360 Sesvete</div>
          <div>
            <a href={`tel:${PHONE_MOBILE_TEL}`} className="hover:text-[#FF3B30]">
              Mob: {PHONE_MOBILE}
            </a>
            {" · "}
            <a href={`tel:${PHONE_LAND_TEL}`} className="hover:text-[#FF3B30]">
              Tel: {PHONE_LAND}
            </a>
          </div>
          <div>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[#FF3B30]">
              {CONTACT_EMAIL}
            </a>
          </div>
          <div>Pon–Pet · 09:00–17:00 · Sub · 09:00–13:00</div>
        </div>
      </div>
    </div>
    <div
      className="border-t py-6 container-pad text-center text-zinc-600 text-xs uppercase tracking-widest"
      style={{ borderColor: "var(--mm-border)" }}
    >
      © {new Date().getFullYear()} Mikra Matik Autodijelovi d.o.o. — Sva prava pridržana.
    </div>
  </footer>
);

function App() {
  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <Header />
      <main>
        <Hero />
        <TrustStrip />
        <Webkatalog />
        <About />
        <Catalog />
        <InquiryForm />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
